import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useModulos } from "@/hooks/use-modulos";
import { useMetodosPagamento, type MetodoPagamento } from "@/hooks/use-metodos-pagamento";
import { apiRequest } from "@/lib/queryClient";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/utils";
import {
  FileText,
  CreditCard,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  ExternalLink,
  Loader2,
} from "lucide-react";

type Fatura = {
  id: string;
  numero: number;
  mes: number;
  ano: number;
  status: string;
  valorTotal: string;
  multa: string | null;
  juros: string | null;
  vencimento: string;
  asaasPaymentId: string | null;
  asaasInvoiceUrl: string | null;
  metodoPagamento: string | null;
  pagoEm: string | null;
  criadoEm: string;
  asaasCobrancaId?: string | null;
};

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType }> = {
  pendente: { label: "Pendente", variant: "secondary", icon: Clock },
  pago: { label: "Pago", variant: "default", icon: CheckCircle2 },
  vencido: { label: "Vencido", variant: "destructive", icon: AlertTriangle },
  atrasada: { label: "Atrasada", variant: "destructive", icon: AlertTriangle },
  cancelado: { label: "Cancelado", variant: "outline", icon: XCircle },
  bloqueada: { label: "Bloqueada", variant: "destructive", icon: XCircle },
};

const METODOS_PAGAMENTO = [
  { value: "pix", label: "PIX" },
  { value: "boleto", label: "Boleto Bancário" },
  { value: "cartao", label: "Cartão de Crédito" },
];

function FaturaStatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, variant: "outline", icon: Clock };
  const Icon = cfg.icon;
  return (
    <Badge variant={cfg.variant} className="gap-1">
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

function totalFatura(f: Fatura) {
  return Number(f.valorTotal) + Number(f.multa ?? 0) + Number(f.juros ?? 0);
}

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

export default function MinhasFaturas() {
  const { toast } = useToast();
  const { modulos, isLoading: modulosLoading } = useModulos();
  const anoAtual = new Date().getFullYear();

  const [filtroAno, setFiltroAno] = useState(String(anoAtual));
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [faturaParaPagar, setFaturaParaPagar] = useState<Fatura | null>(null);
  const [metodoPagamento, setMetodoPagamento] = useState("pix");
  const [cotacaoFatura, setCotacaoFatura] = useState<{ id: string; baseCents: number; processingFeeCents: number; totalCents: number } | null>(null);

  const anos = Array.from({ length: 3 }, (_, i) => String(anoAtual - i));

  // Determina modo: pré-pago (Owner vê suas faturas) vs pós-pago (Parceiro vê faturas_parceiro)
  const isPosPago = !modulos.creditosPrePagos;
  // Só o fluxo pós-pago (cotação Asaas) tem allowlist por ambiente; a rota legada do pré-pago segue como era.
  const metodosPermitidos = useMetodosPagamento(isPosPago);
  const metodosDisponiveis = METODOS_PAGAMENTO.filter((m) =>
    !isPosPago || metodosPermitidos.includes(m.value === "cartao" ? "cartao_credito" : (m.value as MetodoPagamento)));

  const { data: faturas = [], isLoading } = useQuery<Fatura[]>({
    queryKey: ["minhas-faturas", filtroAno, filtroStatus, isPosPago],
    queryFn: async () => {
      const params = new URLSearchParams({ ano: filtroAno });
      if (filtroStatus !== "todos") params.set("status", filtroStatus);
      // Em pós-pago, Parceiro vê suas próprias faturas
      const endpoint = isPosPago ? "/api/minhas-faturas-parceiro" : "/api/minhas-faturas";
      const res = await apiRequest("GET", `${endpoint}?${params}`);
      return res.json();
    },
    enabled: !modulosLoading,
  });

  const iniciarPagamento = useMutation({
    mutationFn: async ({ faturaId, metodo }: { faturaId: string; metodo: string }) => {
      if (isPosPago) {
        if (!cotacaoFatura) {
          const quote = await apiRequest("POST", `/api/asaas/cotacoes/faturas/parceiro/${faturaId}`, {
            method: metodo === "cartao" ? "cartao_credito" : metodo, installments: 1,
          }).then((r) => r.json());
          return { quote };
        }
        return apiRequest("POST", `/api/asaas/cotacoes/${cotacaoFatura.id}/confirmar`, {}).then((r) => r.json());
      }
      const res = await apiRequest("POST", `/api/minhas-faturas/${faturaId}/iniciar-pagamento`, {
        metodo_pagamento: metodo,
        app_url: window.location.origin,
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.quote) { setCotacaoFatura(data.quote); return; }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    },
    onError: (error) => {
      toast({ title: "Erro ao iniciar pagamento", description: apiErrorMessage(error), variant: "destructive" });
    },
  });

  const podePagar = (f: Fatura) =>
    ["pendente", "vencido", "atrasada"].includes(f.status);

  const totalPendente = faturas
    .filter((f) => ["pendente", "vencido", "atrasada"].includes(f.status))
    .reduce((acc, f) => acc + totalFatura(f), 0);

  const totalPago = faturas
    .filter((f) => f.status === "pago")
    .reduce((acc, f) => acc + totalFatura(f), 0);

  return (
    <Shell>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Minhas Faturas</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isPosPago
                ? "Faturas emitidas pela Vistoriadora para você"
                : "Faturas emitidas pelo Owner para sua empresa"}
            </p>
          </div>
        </div>

        {/* Cards de resumo */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">A pagar</p>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(totalPendente)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pago em {filtroAno}</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(totalPago)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <div className="flex gap-3 flex-wrap">
          <Select value={filtroAno} onValueChange={setFiltroAno}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {anos.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                <SelectItem key={v} value={v}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Lista de faturas */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : faturas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Nenhuma fatura encontrada</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {faturas.map((fatura) => (
              <Card key={fatura.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          Fatura #{String(fatura.numero).padStart(3, "0")}/{fatura.ano}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {MESES[fatura.mes - 1]} · Vencimento: {formatDate(fatura.vencimento)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <p className="font-bold text-lg">
                          {formatCurrency(totalFatura(fatura))}
                        </p>
                        {(Number(fatura.multa) > 0 || Number(fatura.juros) > 0) && (
                          <p className="text-xs text-destructive">
                            + multa/juros
                          </p>
                        )}
                      </div>
                      <FaturaStatusBadge status={fatura.status} />
                      {podePagar(fatura) && (
                        <Button
                          size="sm"
                          className="gap-2"
                          onClick={() => {
                            setCotacaoFatura(null);
                            setFaturaParaPagar(fatura);
                          }}
                        >
                          <CreditCard className="w-4 h-4" />
                          {isPosPago ? "Gerar Cobrança" : "Pagar"}
                        </Button>
                      )}
                      {fatura.status === "pago" && fatura.pagoEm && (
                        <p className="text-xs text-muted-foreground whitespace-nowrap">
                          Pago em {new Date(fatura.pagoEm).toLocaleDateString("pt-BR")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Detalhes de multa/juros */}
                  {(Number(fatura.multa) > 0 || Number(fatura.juros) > 0) && (
                    <div className="mt-3 pt-3 border-t text-sm text-muted-foreground flex gap-6">
                      <span>Principal: {formatCurrency(Number(fatura.valorTotal))}</span>
                      {Number(fatura.multa) > 0 && (
                        <span className="text-destructive">
                          Multa: {formatCurrency(Number(fatura.multa))}
                        </span>
                      )}
                      {Number(fatura.juros) > 0 && (
                        <span className="text-destructive">
                          Juros: {formatCurrency(Number(fatura.juros))}
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog de pagamento (pré-pago only) */}
      <Dialog open={!!faturaParaPagar} onOpenChange={() => setFaturaParaPagar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pagar Fatura</DialogTitle>
            <DialogDescription>
              {faturaParaPagar && (
                <>
                  Fatura #{String(faturaParaPagar.numero).padStart(3, "0")}/{faturaParaPagar.ano} ·{" "}
                  <strong>{formatCurrency(cotacaoFatura ? cotacaoFatura.totalCents / 100 : totalFatura(faturaParaPagar))}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Forma de pagamento</label>
              <Select value={metodoPagamento} onValueChange={setMetodoPagamento}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {metodosDisponiveis.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-sm text-muted-foreground">
              {cotacaoFatura
                ? `Base ${formatCurrency(cotacaoFatura.baseCents / 100)} + taxa de processamento ${formatCurrency(cotacaoFatura.processingFeeCents / 100)}.`
                : "Calcule o total vigente antes de confirmar o pagamento."}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFaturaParaPagar(null)}>
              Cancelar
            </Button>
            <Button
              className="gap-2"
              disabled={iniciarPagamento.isPending}
              onClick={() => {
                if (!faturaParaPagar) return;
                iniciarPagamento.mutate({
                  faturaId: faturaParaPagar.id,
                  metodo: metodoPagamento,
                });
              }}
            >
              {iniciarPagamento.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {isPosPago && !cotacaoFatura ? "Calcular total" : "Ir para pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
